import {buildMessagePatcherFactory} from '../../helpers/models/messagePatchHelpers';
import {ThunkAction} from '../../store/appThunk';
import {inviteToConversationAction} from '../../store/data/conversations/inviteToConversationAction';
import {getConversations, getMessages} from '../../store/data/dataSelectors';
import {saveMessageAsyncAction} from '../../store/data/messages/saveMessageActions';
import {CellsContext} from '../../store/panels/cells/context/cellsContext';

/*
 * Task.
 */

export interface ShareMessageOptions {
  trackerIds?: ReadonlyArray<number | string>;
  commentText?: string;
  getVersion: () => string | undefined;
  setVersion?: (version?: string) => void;
}

export function shareMessageTask(
  cellsContext: CellsContext,
  messageUid: string,
  options: ShareMessageOptions,
): ThunkAction {
  const {trackerIds, commentText, getVersion, setVersion} = options;

  return async (dispatch, getState) => {
    // We filter out non-integer (string) IDs, because they're guest emails. Guests can track a conversation,
    // but can't author drafts, so we filter to reflect this.
    const addAuthorIds = trackerIds?.filter(isTrackerIdAnAuthorId);

    // Make sure the draft message is persisted and shared.
    const patchMessage = buildMessagePatcherFactory()({
      addAuthorIds,
      newIsShared: true,
    });
    const saveMessageAction = saveMessageAsyncAction(messageUid, {patchMessage, getVersion, setVersion});
    const {queueDrainPromise} = await dispatch(saveMessageAction);
    await queueDrainPromise; // Wait for the state to be completely updated.

    // If there are no tracker modifications to be made, we can return.
    if (!trackerIds || trackerIds.length === 0) {
      return;
    }

    const messages = getMessages(getState());
    const message = messages[messageUid];
    if (!message) {
      return;
    }

    const conversations = getConversations(getState());
    const conversation = conversations[message.conversationId];
    if (!conversation) {
      return;
    }

    // Mention and update trackers.
    await dispatch(inviteToConversationAction(cellsContext, conversation, trackerIds, commentText));
  };
}

function isTrackerIdAnAuthorId(trackerId: string | number): trackerId is number {
  return Number.isInteger(trackerId);
}
